import Cookies from 'js-cookie';
import axios from 'axios';
import { logWarn } from 'logging';

import { CSRF_TOKEN_COOKIE_KEY } from 'constants/cookies';
import { HTTP_REQUEST_METHODS } from 'api/constants';
import { errorTypes } from 'catering/constants/errors';

class RequestError extends Error {
  constructor(message, code, stack) {
    super(message);
    this.message = message;
    this.code = code;
    this.stack = stack;
  }
}

function restClient(
  endpoint,
  { data, headers: customHeaders, ...customConfig } = {}
) {
  const config = {
    data,
    headers: {
      'X-CSRFToken': Cookies.get(CSRF_TOKEN_COOKIE_KEY),
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json',
      ...customHeaders
    },
    withCredentials: true,
    ...customConfig
  };

  return axios(endpoint, config)
    .then(async response => {
      const { data } = await response;
      return data;
    })
    .catch(error => {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const { data } = error.response;

        if (typeof data === 'string') {
          if (data.trim().length === 0) {
            logWarn('No error message.', {
              data: config.data,
              customHeaders,
              customConfig
            });
          }
          throw new RequestError(
            data,
            errorTypes.INTERNAL_SERVER_ERROR,
            error.stack
          );
        }

        if (!data.error && !data.error_code) {
          throw new RequestError(JSON.stringify(data), null, error.stack);
        }

        throw new RequestError(data?.error, data?.error_code, error.stack);
      }

      if (error.code === errorTypes.REQUEST_ABORTED) {
        // We don't want to get sentry alerts when the request is aborted
        return;
      }

      /*
        The request was either:
          a. made but no response was received
          b. something happened in setting up the request that triggered an Error
       */
      throw new RequestError(error, errorTypes.UNKNOWN_ERROR, error.stack);
    });
}

export const rest = {
  get: (endpoint, options) => {
    return restClient(endpoint, {
      ...options,
      method: HTTP_REQUEST_METHODS.GET
    });
  },
  patch: (endpoint, options) => {
    return restClient(endpoint, {
      ...options,
      method: HTTP_REQUEST_METHODS.PATCH
    });
  },
  post: (endpoint, options) => {
    return restClient(endpoint, {
      ...options,
      method: HTTP_REQUEST_METHODS.POST
    });
  },
  put: (endpoint, options) => {
    return restClient(endpoint, {
      ...options,
      method: HTTP_REQUEST_METHODS.PUT
    });
  },
  delete: (endpoint, options) => {
    return restClient(endpoint, {
      ...options,
      method: HTTP_REQUEST_METHODS.DELETE
    });
  }
};
